import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AiAskDebugInfo, AiAskRequest, AiAskResponse, AiSource, NotesAskMode, NotesAskRequestV2, NotesAskResponse, RagAskResponse, RagService } from './rag.service';

export type AiAskScope = NotesAskMode;

export interface CurrentNoteContext {
  noteId?: string;
  title?: string;
  descriptionHtml: string;
  commentsHtml: string;
}

export interface AiDialogContext {
  currentNote?: CurrentNoteContext;
  filteredNoteIds?: string[];
}

export interface AiSessionState {
  isOpen: boolean;
  query: string;
  answer: string;
  usedNoteIds: string[];
  sources: AiSource[];
  debugInfo: AiAskDebugInfo | null;
  requestId?: string;
  includeDebug: boolean;
  isLoading: boolean;
  error: string | null;
  askScope: AiAskScope;
  currentNoteContext?: CurrentNoteContext;
  filteredNoteIds: string[];
  answerSource?: string;
  model?: string;
  generatedAt?: string;
  selectedReferenceNoteId?: string;
}

const INITIAL_STATE: AiSessionState = {
  isOpen: false,
  query: '',
  answer: '',
  usedNoteIds: [],
  sources: [],
  debugInfo: null,
  includeDebug: false,
  isLoading: false,
  error: null,
  askScope: 'all_notes',
  currentNoteContext: undefined,
  filteredNoteIds: [],
  answerSource: undefined,
  model: undefined,
  generatedAt: undefined,
  selectedReferenceNoteId: undefined,
};

/**
 * Singleton service managing AI chat session state across route navigation.
 * Ensures the AI dialog persists and maintains its state when navigating away and back.
 */
@Injectable({
  providedIn: 'root',
})
export class AiSessionService {
  private readonly stateSubject = new BehaviorSubject<AiSessionState>(INITIAL_STATE);
  readonly state$ = this.stateSubject.asObservable();

  constructor(private ragService: RagService) {}

  // Getters
  private getState(): AiSessionState {
    return this.stateSubject.getValue();
  }

  // Public actions
  openDialog(context?: AiDialogContext): void {
    const state = this.getState();
    const normalizedCurrentNote = this.normalizeCurrentNoteContext(context?.currentNote);
    const normalizedFilteredIds = this.uniqueNoteIds(this.normalizeNoteIds(context?.filteredNoteIds));

    let nextScope = state.askScope;
    if (nextScope === 'single_note' && !normalizedCurrentNote) {
      nextScope = 'all_notes';
    }
    if (nextScope === 'filtered_notes' && normalizedFilteredIds.length === 0) {
      nextScope = 'all_notes';
    }
    if (nextScope === 'all_notes') {
      if (normalizedCurrentNote) {
        nextScope = 'single_note';
      } else if (normalizedFilteredIds.length > 0) {
        nextScope = 'filtered_notes';
      }
    }

    this.stateSubject.next({
      ...state,
      isOpen: true,
      askScope: nextScope,
      currentNoteContext: normalizedCurrentNote,
      filteredNoteIds: normalizedFilteredIds,
    });
  }

  closeDialog(): void {
    const state = this.getState();
    // Only close the dialog, preserve all AI results
    this.stateSubject.next({ ...state, isOpen: false });
  }

  resetSession(): void {
    this.stateSubject.next(INITIAL_STATE);
  }

  clearResults(): void {
    const state = this.getState();
    this.stateSubject.next({
      ...state,
      query: '',
      answer: '',
      usedNoteIds: [],
      sources: [],
      debugInfo: null,
      error: null,
      answerSource: undefined,
      model: undefined,
      generatedAt: undefined,
    });
  }

  setIncludeDebug(value: boolean): void {
    const state = this.getState();
    this.stateSubject.next({ ...state, includeDebug: value });
  }

  setQuery(query: string): void {
    const state = this.getState();
    this.stateSubject.next({ ...state, query });
  }

  setSelectedReferenceNoteId(noteId?: string): void {
    const state = this.getState();
    this.stateSubject.next({ ...state, selectedReferenceNoteId: noteId });
  }

  setAskScope(scope: AiAskScope): void {
    const state = this.getState();
    if (scope === 'single_note' && !this.canAskCurrentNote(state)) {
      return;
    }
    if (scope === 'filtered_notes' && !this.canAskFilteredNotes(state)) {
      return;
    }
    this.stateSubject.next({ ...state, askScope: scope });
  }

  async askQuestion(question: string): Promise<void> {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion) {
      const state = this.getState();
      this.stateSubject.next({
        ...state,
        error: 'Question required. Please enter a question before submitting.',
      });
      return;
    }

    let state = this.getState();
    this.stateSubject.next({
      ...state,
      query: trimmedQuestion,
      answer: '',
      usedNoteIds: [],
      sources: [],
      debugInfo: null,
      isLoading: true,
      error: null,
      answerSource: undefined,
      model: undefined,
      generatedAt: undefined,
    });

    try {
      const activeState = this.getState();

      if (activeState.askScope === 'all_notes') {
        await this.askViaNewEndpoint(trimmedQuestion, activeState);
      } else {
        await this.askViaLegacyEndpoint(trimmedQuestion, activeState);
      }
    } catch (err: any) {
      const errorMsg = this.formatAskError(err);
      state = this.getState();
      this.stateSubject.next({ ...state, isLoading: false, error: errorMsg });
    }
  }

  private async askViaNewEndpoint(question: string, activeState: AiSessionState): Promise<void> {
    const today = new Date();
    const referenceDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const request: AiAskRequest = {
      question,
      reference_date: referenceDate,
      use_tools: true,
      include_debug: activeState.includeDebug,
      max_tool_calls: 5,
    };

    const rawResp = await this.ragService.askNotesAi(request).toPromise();

    // The /api/ai/ endpoints wrap their payload in an ApiEnvelope: { status, data: <actual response> }.
    // Fall back to the raw response if it happens to be flat (future-proof).
    const resp: AiAskResponse | null = (rawResp as any)?.data ?? rawResp ?? null;

    const answer = resp?.answer?.trim() || 'No answer returned.';
    const sources: AiSource[] = Array.isArray(resp?.sources) ? resp!.sources! : [];
    const usedNoteIds = sources.map((s) => s.note_id).filter((id): id is string => !!id);

    const state = this.getState();
    this.stateSubject.next({
      ...state,
      answer,
      sources,
      usedNoteIds,
      debugInfo: resp?.debug ?? null,
      requestId: resp?.request_id,
      isLoading: false,
      error: null,
      answerSource: 'all_notes',
      model: resp?.debug?.model,
    });
  }

  private async askViaLegacyEndpoint(question: string, activeState: AiSessionState): Promise<void> {
    const payload = this.buildAskPayload(question, activeState);
    const resp = await this.ragService.askNotes(payload).toPromise();
    const normalized = this.unwrapNotesAskResponse(resp);
    const answer = this.extractAnswer(normalized);
    const usedNoteIds = this.extractUsedNoteIds(normalized);

    const state = this.getState();
    this.stateSubject.next({
      ...state,
      answer,
      usedNoteIds,
      sources: [],
      debugInfo: null,
      isLoading: false,
      error: null,
      answerSource: String((normalized as any)?.source ?? payload.source.mode),
      model: typeof (normalized as any)?.model === 'string' ? (normalized as any).model : undefined,
      generatedAt: typeof (normalized as any)?.generated_at === 'string' ? (normalized as any).generated_at : undefined,
    });
  }

  private buildAskPayload(question: string, state: AiSessionState): NotesAskRequestV2 {
    if (state.askScope === 'single_note') {
      const currentNote = this.normalizeCurrentNoteContext(state.currentNoteContext);
      if (!currentNote) {
        throw new Error('Ask current note is unavailable because no note context was provided.');
      }

      return {
        question,
        source: {
          mode: 'single_note',
          note: {
            _id: currentNote.noteId,
            Title: currentNote.title,
            DescriptionHtml: currentNote.descriptionHtml,
            CommentsHtml: currentNote.commentsHtml,
          },
        },
        retrieval: { top_k: 5, prefer_recent: true },
        generation: { temperature: 0.2, max_context_chars: 6000 },
        options: { debug: false },
      };
    }

    if (state.askScope === 'filtered_notes') {
      const noteIds = this.uniqueNoteIds(this.normalizeNoteIds(state.filteredNoteIds));
      if (noteIds.length === 0) {
        throw new Error('Filtered notes mode requires note IDs from an active Home filter.');
      }

      return {
        question,
        source: {
          mode: 'filtered_notes',
          note_ids: noteIds,
        },
        retrieval: { top_k: 6, prefer_recent: false },
        generation: { temperature: 0.2, max_context_chars: 7000 },
        options: { debug: false },
      };
    }

    return {
      question,
      source: { mode: 'all_notes' },
      retrieval: { top_k: 8, prefer_recent: true },
      generation: { temperature: 0.2, max_context_chars: 7000 },
      options: { debug: false },
    };
  }

  private unwrapNotesAskResponse(resp?: NotesAskResponse | null): any {
    if (!resp) {
      return {};
    }
    const envelopeData = (resp as any)?.data;
    return envelopeData && typeof envelopeData === 'object' ? envelopeData : resp;
  }

  private extractAnswer(data?: RagAskResponse | null): string {
    if (!data) {
      return 'No answer returned.';
    }
    const candidates = [data.answer, data.response, data.result, data.message, data];
    for (const candidate of candidates) {
      const normalized = this.normalizeAnswer(candidate);
      if (normalized) {
        return normalized;
      }
    }
    return 'No answer returned.';
  }

  private normalizeCurrentNoteContext(context?: CurrentNoteContext): CurrentNoteContext | undefined {
    if (!context) {
      return undefined;
    }

    const descriptionHtml = String(context.descriptionHtml ?? '').trim();
    const commentsHtml = String(context.commentsHtml ?? '').trim();

    if (!descriptionHtml && !commentsHtml) {
      return undefined;
    }

    return {
      noteId: typeof context.noteId === 'string' && context.noteId.trim() ? context.noteId.trim() : undefined,
      title: typeof context.title === 'string' && context.title.trim() ? context.title.trim() : undefined,
      descriptionHtml,
      commentsHtml,
    };
  }

  private canAskCurrentNote(state: AiSessionState): boolean {
    const note = this.normalizeCurrentNoteContext(state.currentNoteContext);
    return !!note;
  }

  private canAskFilteredNotes(state: AiSessionState): boolean {
    return this.uniqueNoteIds(this.normalizeNoteIds(state.filteredNoteIds)).length > 0;
  }

  private extractUsedNoteIds(data?: RagAskResponse | null): string[] {
    if (!data) {
      return [];
    }
    // Strict mode: show only notes that are explicitly cited in the final answer payload.
    const candidates: any[] = [
      (data as any).cited_note_ids,
      (data as any).citedNoteIds,
      Array.isArray((data as any).citations)
        ? (data as any).citations.map((c: any) => c?.note_id ?? c?.id)
        : undefined,
      Array.isArray((data as any).sources?.citations)
        ? (data as any).sources.citations.map((c: any) => c?.note_id ?? c?.id)
        : undefined,
      (data as any).sources?.cited_note_ids,
      (data as any).sources?.citedNoteIds,
    ];

    for (const candidate of candidates) {
      const list = this.uniqueNoteIds(this.normalizeNoteIds(candidate));
      if (list.length) {
        return list;
      }
    }

    return [];
  }

  private normalizeAnswer(value: any): string | null {
    if (value == null) {
      return null;
    }

    if (typeof value === 'string') {
      const cleaned = this.stripTrailingReferences(value);
      return cleaned ? cleaned : null;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (typeof value === 'object') {
      const nested = (value as any).text ?? (value as any).content ?? (value as any).message ?? (value as any).answer;

      if (typeof nested === 'string' && nested.trim()) {
        const cleaned = this.stripTrailingReferences(nested);
        return cleaned ? cleaned : null;
      }

      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }

    return null;
  }

  private stripTrailingReferences(text: string): string {
    const normalized = String(text ?? '').replace(/\r\n/g, '\n').trim();
    if (!normalized) {
      return '';
    }

    // Remove trailing reference blocks rendered in model output.
    const headingMatch = normalized.match(/\n(?:references|sources|citations)\s*:?\s*\n[\s\S]*$/i);
    if (headingMatch && headingMatch.index != null) {
      return normalized.slice(0, headingMatch.index).trim();
    }

    return normalized;
  }

  private normalizeNoteIds(value: any): string[] {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.map((v) => (typeof v === 'string' ? v : v?.toString?.() ?? '')).filter((v) => !!v);
    }

    if (typeof value === 'string') {
      return value ? [value] : [];
    }

    return [];
  }

  private uniqueNoteIds(noteIds: string[]): string[] {
    return [...new Set(noteIds.map((id) => String(id ?? '').trim()).filter((id) => !!id))];
  }

  private formatAskError(err: any): string {
    const status = Number(err?.status ?? 0);
    const backendError = err?.error?.error;
    const backendMessage =
      (typeof backendError === 'string' && backendError) ||
      (typeof backendError?.message === 'string' && backendError.message) ||
      (typeof err?.error?.message === 'string' && err.error.message) ||
      (typeof err?.message === 'string' && err.message) ||
      '';

    if (status === 0) {
      return 'Could not reach the AI service. Check your connection and try again.';
    }
    if (status === 429) {
      return backendMessage || 'AI rate limit exceeded. Please try again later.';
    }
    if (status === 502 || status === 503 || status === 504) {
      return 'The AI service is temporarily unavailable (server error). Please try again in a moment.';
    }
    if (status === 405) {
      return 'Ask Notes endpoint is not enabled for POST at /api/notes/ask (405 Method Not Allowed).';
    }

    if (backendMessage) {
      return backendMessage;
    }

    if (backendError && typeof backendError === 'object' && !Array.isArray(backendError)) {
      const details = Object.entries(backendError)
        .filter(([, value]) => typeof value !== 'object' || value === null)
        .map(([key, value]) => `${key}: ${String(value)}`)
        .join(' | ');
      if (details) {
        return details;
      }
    }

    return 'An error occurred while asking the question.';
  }
}
